import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { registerUser, clearError } from '../../store/slices/authSlice';
import PhoneInput from '../../components/common/PhoneInput';
import toast from 'react-hot-toast';
import { useTranslation } from '../../hooks/useTranslation';
import PasswordStrengthIndicator from '../../components/common/PasswordStrengthIndicator';

const VendorRegisterPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loading, error, isAuthenticated } = useAppSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    countryCode: 'PK',
    shopName: '',
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/vendor/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhoneChange = (phoneValue: string, countryCode?: string) => {
    setFormData({
      ...formData,
      phone: phoneValue,
      countryCode: countryCode || formData.countryCode,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword || !formData.phone || !formData.shopName) {
      toast.error('Please fill in all fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.phone && !/^\+[1-9]\d{1,14}$/.test(formData.phone.replace(/\s+/g, ''))) {
      toast.error('Please enter a valid phone number with country code');
      return;
    }

    // Password strength validation
    const isStrongPassword = 
      formData.password.length >= 8 &&
      /[A-Z]/.test(formData.password) &&
      /[a-z]/.test(formData.password) &&
      /[0-9]/.test(formData.password) &&
      /[^A-Za-z0-9]/.test(formData.password);

    if (!isStrongPassword) {
      toast.error('Please choose a stronger password');
      return;
    }

    try {
      // Dispatch register with vendor specific data
      // Note: We need to ensure the authSlice/registerUser action supports these extra fields
      // or we might need to modify it. For now assuming we pass them and backend handles it.
      // We are adding role: 'vendor' implicitly here. 
      // Ideally the backend registration endpoint should accept 'role' and 'vendorDetails'.
      
      const registerData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        role: 'vendor',
        vendorDetails: {
          shopName: formData.shopName
        }
      };

      // @ts-ignore - Assuming registerUser can take extra args or we will fix types later
      const result = await dispatch(registerUser(registerData)).unwrap();
      
      if (result.vendorStatus === 'pending') {
         toast.success('Registration successful! Your vendor application is pending approval.');
         navigate('/vendor/dashboard'); // Dashboard will show pending state
         return;
      }

      if (result.emailVerificationRequired) {
        toast.success('Registration successful! Please check your email for verification link.');
        navigate('/email-verification', { state: { email: formData.email } });
        return;
      }

      if (result.phoneVerificationRequired) {
        if (result.smsSuccess) {
          toast.success('Registration successful! SMS sent to your phone.');
        } else {
          toast.success('Registration successful! Please check your phone for verification code.');
        }
        navigate('/verify-phone', { 
          state: { 
            phoneNumber: formData.phone,
            devCode: result.devCode 
          }
        });
        return; // Add return to prevent fall-through
      } 
      
      // Default success case
      toast.success('Vendor Registration Successful!');
      navigate('/vendor/dashboard');
    } catch (error) {
      // Error is handled by the useEffect above
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-blue-900 dark:text-white">
            Register as a Vendor
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            {'Start selling your products today!'}
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="shopName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Shop Name
                </label>
                <input
                  id="shopName"
                  name="shopName"
                  type="text"
                  required
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700"
                  placeholder="e.g. Best Shoes Co"
                  value={formData.shopName}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.name')}
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700"
                  placeholder={t('auth.name')}
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
              
              <PhoneInput
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder={t('auth.phone')}
                required
                label={t('auth.phone')}
                className=""
              />
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.email')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700"
                  placeholder={t('auth.email')}
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.password')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700"
                  placeholder={t('auth.password')}
                  value={formData.password}
                  onChange={handleChange}
                />
                <PasswordStrengthIndicator password={formData.password} />
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('auth.confirmPassword')}
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm dark:bg-gray-700"
                  placeholder={t('auth.confirmPassword')}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-900 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  'Register as Vendor'
                )}
              </button>
            </div>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400">{t('auth.orContinue')}</span>
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => {
                    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                    window.location.href = `${API_URL}/api/auth/google?role=vendor`;
                  }}
                  className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span className="ml-2">{t('auth.google')}</span>
                </button>
              </div>
            </div>
            
            <div className="text-center mt-4">
               <p className="text-sm text-gray-600 dark:text-gray-400">
                 Already a vendor?{' '}
                 <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400">
                   Login here
                 </Link>
               </p>
               <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                 Want to buy instead?{' '}
                 <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400">
                   Register as Customer
                 </Link>
               </p>
            </div>
        </form>
      </div>
    </div>
  );
};

export default VendorRegisterPage;
